import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder } from '@angular/forms';
import { BaseChildComponent } from '@components/_base/base-child/base-child.component';
import { NomenclatureService } from '@services/nomenclature.service';
import { PromotionService } from '@app/promotions/_services/promotion.service';
import { TranslateService } from '@ngx-translate/core';
import { catchError, take, tap } from 'rxjs/operators';
import { EMPTY, forkJoin } from 'rxjs';
import { PaymentType } from '@models/payment-type.model';
import { PromotionPaymentType } from '@app/promotions/_models/promotion-payment-type.model';
import { displayError } from '@app/_utils/error-util';

@Component({
  selector: 'app-promotion-payment-types',
  templateUrl: './promotion-payment-types.component.html',
  styleUrls: ['./promotion-payment-types.component.css']
})
export class PromotionPaymentTypesComponent extends BaseChildComponent implements OnInit, OnDestroy {
  // Constants
  readonly ACTIVE_STATUS_CODE = 'ACTIVE';

  // Property decorators
  @Input() statusCode: string | undefined;
  @Input() isDisabled: boolean;

  // Form
  paymentTypesForm = this.formBuilder.group({
    paymentTypes: this.formBuilder.array([])
  });

  constructor(
    private formBuilder:          FormBuilder,
    private translateService:     TranslateService,
    private promotionService:     PromotionService,
    private nomenclatureService:  NomenclatureService
  ) {
    super();
  }

  ngOnInit() {
    this.showValidationsSubscription = this.showValidations$.subscribe({
      next: (data) => this.showValidations = data,
    });
  }

  ngOnDestroy() {
    this.showValidationsSubscription?.unsubscribe();
  }

  initialize() {
    if (this.isInitialized) return;
    this.isInitialized = true;

    return forkJoin([
      this.promotionService.findPaymentTypesByPromotionId(this.parentId),
      this.nomenclatureService.getPaymentTypes()
    ]).pipe(
      tap(([existingPaymentTypes, allPaymentTypes]) => {
        const selectedIds = existingPaymentTypes?.map(ppt => ppt.paymentType.id) ?? [];

        allPaymentTypes.forEach(pt => {
          this.paymentTypesForms.push(this.formBuilder.group({
            paymentType: [pt],
            isSelected: [selectedIds.includes(pt.id)]
          }));
        });

        if (this.statusCode == this.ACTIVE_STATUS_CODE) {
          this.paymentTypesForm.disable();
        }
      }),
      catchError(err => {
        displayError(err);
        this.errorMessageSubject.next(this.translateService.instant('messages.errorLoadingData'));
        return EMPTY;
      })
    ).pipe(take(1)).toPromise();
  }

  get paymentTypesForms(): FormArray {
    return this.paymentTypesForm.get('paymentTypes') as FormArray;
  }

  validatePaymentTypes(): boolean {
    const hasSelected = this.paymentTypesForms.controls.some(c => c.get('isSelected')?.value === true);
    if (!hasSelected) {
      this.isOpened = true;
    }
    return hasSelected;
  }

  constructPaymentTypes(): PromotionPaymentType[] | null {
    if (!this.isInitialized) {
      return null;
    }

    return this.paymentTypesForms.controls
      .filter(c => c.get('isSelected')?.value === true)
      .map(c => ({
        paymentType: c.get('paymentType')?.value as PaymentType
      }));
  }
}
